import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Leaf, Recycle, Zap, Truck, FileCheck, Factory } from 'lucide-react';

const sustainabilityItems = [
  {
    icon: Zap,
    titleKey: 'renewableEnergy',
    descKey: 'renewableEnergyDesc',
    stat: '30%',
    statLabelKey: 'renewable',
  },
  {
    icon: Factory,
    titleKey: 'productOptimization',
    descKey: 'productOptimizationDesc',
    stat: '15%',
    statLabelKey: 'wasteReduction',
  },
  {
    icon: Leaf,
    titleKey: 'responsibleMaterials',
    descKey: 'responsibleMaterialsDesc',
    stat: '90%',
    statLabelKey: 'certifiedSuppliers',
  },
  {
    icon: Recycle,
    titleKey: 'wasteManagement',
    descKey: 'wasteManagementDesc',
    stat: '95%',
    statLabelKey: 'recyclingRate',
  },
  {
    icon: Truck,
    titleKey: 'efficientLogistics',
    descKey: 'efficientLogisticsDesc',
    stat: '20%',
    statLabelKey: 'co2Reduction',
  },
  {
    icon: FileCheck,
    titleKey: 'compliance',
    descKey: 'complianceDesc',
    stat: 'ISO',
    statLabel: '14001:2015',
  },
];

export const Sustainability = () => {
  const { t } = useLanguage();

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="sustainability-page">
      {/* Hero */}
      <section className="relative py-20 md:py-32 bg-slate-900 text-white overflow-hidden" data-testid="sustainability-hero">
        <div className="absolute inset-0 opacity-20">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/images/site/sustainability.png`}
            alt="Sustainability"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block bg-green-600 text-white text-sm uppercase tracking-wider font-bold px-4 py-2 mb-6">
              {t('sustainability')}
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-6">
              {t('sustainabilityTitle')}
            </h1>
            <p className="text-slate-300 text-lg md:text-xl leading-relaxed">
              {t('sustainabilitySubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Grid */}
      <section className="py-20 md:py-32" data-testid="sustainability-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sustainabilityItems.map((item, index) => (
              <div
                key={index}
                className="bg-white border border-slate-200 p-8 card-lift"
                data-testid={`sustainability-item-${index}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 bg-green-100 flex items-center justify-center">
                    <item.icon className="h-7 w-7 text-green-600" />
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-3xl font-bold text-green-600">
                      {item.stat}
                    </div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">
                      {item.statLabelKey ? t(item.statLabelKey) : item.statLabel}
                    </div>
                  </div>
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-3">
                  {t(item.titleKey)}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {t(item.descKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="sustainability-commitment">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-green-600 text-sm uppercase tracking-wider font-bold mb-4 block">
                {t('ourCommitment')}
              </span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-slate-900 uppercase tracking-tight mb-6">
                {t('buildingSustainableFuture')}
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {t('sustainabilityCommitmentP1')}
              </p>
              <p className="text-slate-600 text-lg leading-relaxed">
                {t('sustainabilityCommitmentP2')}
              </p>
            </div>
            <div className="relative">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/images/site/sustainability.png`}
                alt="Sustainable Manufacturing"
                className="w-full shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-green-600 text-white p-6">
                <div className="font-heading text-3xl font-bold">2020</div>
                <div className="text-sm uppercase tracking-wider">{t('carbonNeutral')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Goals */}
      <section className="py-20 md:py-32 bg-slate-900 text-white" data-testid="sustainability-goals">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight mb-4">
              {t('our2030Goals')}
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              {t('goalsSubtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { target: '50%', labelKey: 'co2ReductionGoal', descKey: 'from2020Baseline' },
              { target: '100%', labelKey: 'circularSteel', descKey: 'recycledContentTarget' },
              { targetKey: 'zeroWaste', labelKey: 'wasteToLandfill', descKey: 'fullCircularity' },
            ].map((goal, index) => (
              <div key={index} className="text-center p-8 bg-slate-800">
                <div className="font-heading text-5xl md:text-6xl font-bold text-green-500 mb-4">
                  {goal.targetKey ? t(goal.targetKey) : goal.target}
                </div>
                <div className="font-heading text-xl font-bold uppercase tracking-tight mb-2">
                  {t(goal.labelKey)}
                </div>
                <p className="text-slate-400 text-sm">{t(goal.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
